import {
    Component,
    OnInit,
    signal,
    computed,
    ChangeDetectorRef,
    ViewEncapsulation,
    effect,
    ChangeDetectionStrategy,
    viewChild,
} from "@angular/core";

import { TypeManagerDecorator } from "../../main/type.map.service";
import { WorkflowService } from "./service/workflow.service";
import { Workflow } from "./service/Workflow";
import { WorkflowUtil } from "./workflow.util";
import { S25Util } from "../../util/s25-util";
import { WorkflowProcessEditorComponent } from "./workflow.process.editor.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { Table } from "../s25-table/Table";
import { S25TableModule } from "../s25-table/s25.table.module";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { WorkflowDiagramViewerComponent } from "./diagram/workflow.diagram.viewer.component";
import { DraggableDirective } from "../s25-movable/s25.movable.element.directive";
import { ApprovalGroupsStore } from "../approval-groups/approval.groups.store";
import { NgTemplateOutlet } from "@angular/common";
import { AlertService } from "../../services/alert.service";
import { StateService } from "../../services/state.service";

@TypeManagerDecorator("workflow-processes-page")
@Component({
    selector: "workflow-processes-page",
    template: `
        @if (!selectedWorkflow()) {
            <!-- List View -->
            <div class="list-view">
                <div class="header">
                    <h1>Workflows</h1>
                    <button class="aw-button aw-button--primary" (click)="createNewWorkflow()">New Workflow</button>
                </div>
                <ng-container *ngTemplateOutlet="diagramPanel"></ng-container>
                <s25-ng-table
                    #workflowTable
                    [dataSource]="tableConfig"
                    [columnSortable]="true"
                    [hasFilter]="true"
                    [hasRefresh]="true"
                ></s25-ng-table>
            </div>
        } @else {
            <!-- Edit View -->
            <div class="header">
                <h1>{{ isNewWorkflow() ? "New Workflow" : selectedWorkflow()!.workflowProcessName }}</h1>
                <ng-container *ngTemplateOutlet="actions"></ng-container>
            </div>

            <ng-container *ngTemplateOutlet="diagramPanel"></ng-container>

            <workflow-process-editor
                [(workflowProcess)]="selectedWorkflow"
                [workflowProcessId]="selectedWorkflowId()"
                (saved)="onEditorSaved()"
                (cancelled)="cancelEdit()"
            ></workflow-process-editor>
        }

        <ng-template #diagramPanel>
            @if (showDiagram()) {
                <div draggableElement [fitToViewport]="true" class="workflow-diagram-panel">
                    <workflow-diagram-viewer
                        [workflowProcess]="workflowForDiagram()"
                        [(showDiagram)]="showDiagram"
                    ></workflow-diagram-viewer>
                </div>
            }
        </ng-template>

        <ng-template #actions>
            <div class="actions">
                <button
                    class="aw-button aw-button--outline"
                    (click)="toggleDiagram()"
                    [attr.title]="showDiagram() ? 'Hide Diagram' : 'Show Diagram'"
                >
                    {{ showDiagram() ? "Hide" : "Show" }} Diagram
                </button>
            </div>
        </ng-template>
    `,
    styles: `
        .header {
            padding: 20px;
            margin-bottom: 20px;
            background: var(--color--surface-bg);
            border-bottom: 1px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .header h1 {
            margin: 0;
            color: var(--color--text);
            font-size: 24px;
        }

        .actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
        }

        .list-view {
            flex: 1;
            padding: 20px;
        }

        .workflow-diagram-panel {
            height: auto;
            display: flex;
            flex-direction: column;
            overflow: auto;
            background: var(--color--surface-2-bg);
            border-right: 1px solid var(--color--border);
            border-radius: 8px;
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 12%, transparent);
            width: 95vw;
            max-width: 100vw;
        }
    `,
    standalone: true,
    imports: [
        WorkflowProcessEditorComponent,
        S25DropdownPaginatedModule,
        S25TableModule,
        WorkflowDiagramViewerComponent,
        DraggableDirective,
        NgTemplateOutlet,
    ],
    providers: [ApprovalGroupsStore],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowProcessesPageComponent implements OnInit {
    tableComponent = viewChild("workflowTable", { read: S25TableComponent });

    selectedWorkflowId = signal<number | null>(null);
    selectedWorkflow = signal<Workflow.WorkflowProcess | null>(null);
    showDiagram = signal(false);
    /** Workflow shown in diagram when opened from the list (does not open the editor). */
    diagramListWorkflow = signal<Workflow.WorkflowProcess | null>(null);
    tableConfig: Table.DataSource;

    // Computed signal to ensure diagram updates when workflow changes
    workflowForDiagram = computed(() => {
        const workflow = this.selectedWorkflow() ?? this.diagramListWorkflow();
        if (!workflow) return null;

        // Create a copy to avoid mutating the original
        const workflowCopy = {
            ...workflow,
            stages: workflow.stages ? [...workflow.stages] : [],
            stageTransitions: workflow.stageTransitions ? [...workflow.stageTransitions] : [],
        };

        // Ensure stage names are populated on transitions
        if (workflowCopy.stageTransitions && workflowCopy.stages) {
            WorkflowUtil.populateStageNamesOnTransitions(workflowCopy.stageTransitions, workflowCopy.stages);
        }

        return workflowCopy;
    });

    isNewWorkflow = computed(() => !this.selectedWorkflowId());

    constructor(private cd: ChangeDetectorRef) {
        effect(() => {
            if (!this.showDiagram()) this.diagramListWorkflow.set(null);
        });
    }

    ngOnInit() {
        this.initializeTableConfig();
        void this.openWorkflowFromStateParam();
    }

    private async openWorkflowFromStateParam(): Promise<void> {
        const workflowProcessIdParam = StateService.getState()?.params?.workflowProcessId;
        if (!workflowProcessIdParam) {
            return;
        }

        const workflowProcessId = S25Util.parseInt(String(workflowProcessIdParam));
        if (!workflowProcessId) {
            return;
        }

        try {
            const workflow = await WorkflowService.getWorkflowProcess(workflowProcessId);
            await this.selectWorkflow(workflow);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError(error, "Failed to open workflow from link.");
        }
    }

    private initializeTableConfig() {
        const columns: Table.Column[] = [
            { id: "workflowProcessId", header: "ID" },
            { id: "name", header: "Name" },
            { id: "description", header: "Description" },
            { id: "stages", header: "Stages" },
            { id: "actions", header: "Actions" },
            { id: "stageTransitions", header: "Stage Transitions" },
            { id: "enabled", header: "Enabled" },
            GenericTableButtonComponent.Column("Diagram", this.onDiagramClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Edit", this.onEditClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Copy", this.onCopyClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Delete", this.onDeleteClick.bind(this), "danger--outline"),
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getWorkflowProcessesData.bind(this),
        };
    }

    async refresh() {
        // Only refresh table if we're in list mode and the component is available
        if (!this.selectedWorkflow() && this.tableComponent()) {
            this.tableComponent()!.refresh();
        }
        this.cd.detectChanges();
    }

    async getWorkflowProcessesData(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        const processes = await WorkflowService.getWorkflowProcesses();
        const rows: Table.Row[] = processes.map((process) => ({
            id: Number(process.workflowProcessId),
            name: process.workflowProcessName,
            cells: {
                workflowProcessId: { text: process.workflowProcessId },
                name: { text: process.workflowProcessName },
                description: { text: process.description || "No description" },
                stages: { text: process.stages?.length || 0 },
                actions: { text: process.stages?.flatMap((stage) => stage.actions || []).length || 0 },
                stageTransitions: { text: process.stageTransitions?.length || 0 },
                enabled: { text: process.enabled ? "Yes" : "No" },
            },
            data: process,
        }));
        return {
            rows: rows,
            totalRows: rows.length,
        };
    }

    async onEditClick(row: Table.Row) {
        const workflow = row.data as Workflow.WorkflowProcess;
        await this.selectWorkflow(workflow);
    }

    async onDeleteClick(row: Table.Row) {
        const workflowId = row.id as number;
        await this.deleteWorkflow(workflowId);
    }

    async onDiagramClick(row: Table.Row) {
        const workflow = row.data as Workflow.WorkflowProcess;
        this.diagramListWorkflow.set(this.prepareWorkflowForDiagramView(workflow));
        this.showDiagram.set(true);
    }

    async onCopyClick(row: Table.Row) {
        const workflow = row.data as Workflow.WorkflowProcess;
        await this.copyWorkflow(workflow);
    }

    async selectWorkflow(workflow: Workflow.WorkflowProcess) {
        this.diagramListWorkflow.set(null);
        this.selectedWorkflowId.set(workflow.workflowProcessId!);
        this.selectedWorkflow.set(this.prepareWorkflowForDiagramView(workflow));
    }

    /** Deep copy + IDs for diagram rendering (used for editor and list-view diagram). */
    private prepareWorkflowForDiagramView(workflow: Workflow.WorkflowProcess): Workflow.WorkflowProcess {
        const workflowCopy = S25Util.deepCopy(workflow);

        // Ensure states and transitions arrays exist
        if (!workflowCopy.stages) {
            workflowCopy.stages = [];
        }
        if (!workflowCopy.stageTransitions) {
            workflowCopy.stageTransitions = [];
        }

        // Ensure all stages have IDs for diagram rendering
        workflowCopy.stages.forEach((stage, index) => {
            if (!stage.id) {
                stage.id = stage.workflowStageId || -(index + 1);
            }
        });

        // Populate stage names on transitions
        WorkflowUtil.populateStageNamesOnTransitions(workflowCopy.stageTransitions, workflowCopy.stages);

        // Ensure all transitions have IDs for diagram rendering
        workflowCopy.stageTransitions.forEach((transition, index) => {
            if (!transition.id) {
                transition.id = transition.workflowStageTransitionId || -(index + 1);
            }
        });

        return workflowCopy;
    }

    createNewWorkflow() {
        this.selectedWorkflowId.set(null);
        this.diagramListWorkflow.set(null);
        this.showDiagram.set(false);
        this.selectedWorkflow.set({
            workflowProcessName: "",
            description: "",
            enabled: true,
            stages: [],
            stageTransitions: [],
        });
    }

    toggleDiagram() {
        this.showDiagram.update((show) => !show);
    }

    async copyWorkflow(originalWorkflow: Workflow.WorkflowProcess) {
        // Create a deep copy of the workflow
        const copiedWorkflow = S25Util.deepCopy(originalWorkflow);

        // Clear all IDs to ensure they get new ones when saved
        copiedWorkflow.workflowProcessId = undefined;
        copiedWorkflow.workflowProcessName = `${originalWorkflow.workflowProcessName} (Copy)`;

        // Clear stage IDs and reset to new workflow
        if (copiedWorkflow.stages) {
            copiedWorkflow.stages.forEach((stage, stageIndex) => {
                stage.workflowStageId = undefined;
                stage.workflowProcessId = undefined;
                stage.id = -(stageIndex + 1); // Use negative IDs for new stages

                // Clear state IDs
                if (stage.states) {
                    stage.states.forEach((state, stateIndex) => {
                        state.workflowStateId = undefined;
                        state.workflowStageId = undefined;
                        state.workflowProcessId = undefined;
                        state.id = -(stateIndex + 1); // Use negative IDs for new states

                        // Clear action IDs
                        if (stage.actions) {
                            stage.actions.forEach((action, actionIndex) => {
                                action.workflowActionId = undefined;
                                action.workflowStageId = undefined;
                                action.id = -(actionIndex + 1); // Use negative IDs for new actions

                                // Clear action input IDs
                                if (action.inputs) {
                                    action.inputs.forEach((input, inputIndex) => {
                                        input.id = -(inputIndex + 1); // Use negative IDs for new inputs
                                    });
                                }
                            });
                        }
                    });
                }
            });
        }

        // Clear transition IDs
        if (copiedWorkflow.stageTransitions) {
            copiedWorkflow.stageTransitions.forEach((transition, transitionIndex) => {
                transition.workflowStageTransitionId = undefined;
                transition.workflowProcessId = undefined;
                transition.id = -(transitionIndex + 1); // Use negative IDs for new transitions
            });
        }

        // Set up the copied workflow for editing
        this.selectedWorkflowId.set(null); // This makes it a new workflow
        this.diagramListWorkflow.set(null);
        this.showDiagram.set(false);
        this.selectedWorkflow.set(copiedWorkflow);
    }

    cancelEdit() {
        this.selectedWorkflowId.set(null);
        this.selectedWorkflow.set(null);
        this.diagramListWorkflow.set(null);
        this.showDiagram.set(false);
    }

    async onEditorSaved() {
        await this.refresh();
        this.cancelEdit();
    }

    async deleteWorkflow(workflowId: number) {
        const confirmed = await AlertService.confirm("Are you sure you want to delete this workflow?");
        if (!confirmed) {
            return;
        }

        try {
            await WorkflowService.deleteWorkflowProcess(workflowId);
            await this.refresh();

            if (this.selectedWorkflowId() === workflowId) {
                this.cancelEdit();
            }
        } catch (error) {
            S25Util.showError(error);
        }
    }
}
